// store/app.ts - Enhanced version with improved API integration and auth store compatibility
import { create } from 'zustand';
import { persist } from 'zustand/middleware';

// Import types from API for better integration
import type { ApiErrorResponse, NotificationType, NotificationCategory, Priority } from '@/api/types';

interface AppState {
  // UI State
  sidebarOpen: boolean;
  theme: 'light' | 'dark' | 'system';
  
  // Loading states - Enhanced for API integration
  globalLoading: boolean;
  apiLoading: Record<string, boolean>; // Track individual API calls
  
  // Error handling - Enhanced for API errors with improved structure
  globalError: string | null;
  apiErrors: Record<string, string | ApiErrorResponse>; // Support both string and structured API errors
  
  // Notifications - Enhanced for real-time updates with API integration
  unreadCount: number;
  notifications: Array<{
    id: string;
    type: 'success' | 'error' | 'warning' | 'info';
    title: string;
    message: string;
    timestamp: number;
    read: boolean;
    persistent?: boolean;
    // Enhanced fields for API integration
    category?: NotificationCategory;
    priority?: Priority;
    actionUrl?: string;
    actionText?: string;
    relatedEntityId?: string;
    relatedEntityType?: 'correspondence' | 'response' | 'project' | 'user' | 'organization';
  }>;
  
  // API State Management - Enhanced
  lastApiCall: number;
  apiCacheTimestamp: Record<string, number>;
  requestQueue: Array<{ id: string; endpoint: string; retry: number; timestamp: number }>; // Track failed requests for retry
  
  // Feature flags for new functionality - Updated with current needs
  featureFlags: {
    enableRealTimeNotifications: boolean;
    enableAdvancedSearch: boolean;
    enableBatchOperations: boolean;
    enableEmailSync: boolean;
    enableWorkflowAutomation: boolean;
  };
  
  // Session state integration with auth store
  sessionWarningShown: boolean;
  offlineMode: boolean;

  // Onboarding
  showOnboarding: boolean;
  onboardingCompleted: boolean;
}

interface AppActions {
  // Sidebar actions
  toggleSidebar: () => void;
  setSidebarOpen: (open: boolean) => void;
  
  // Theme actions
  setTheme: (theme: 'light' | 'dark' | 'system') => void;
  
  // Loading actions - Enhanced for API integration
  setGlobalLoading: (loading: boolean) => void;
  setApiLoading: (key: string, loading: boolean) => void;
  clearApiLoading: () => void;
  isAnyApiLoading: () => boolean;
  
  // Error actions - Enhanced for API errors with structured support
  setGlobalError: (error: string | null) => void;
  clearGlobalError: () => void;
  setApiError: (key: string, error: string | ApiErrorResponse) => void;
  clearApiError: (key: string) => void;
  clearAllApiErrors: () => void;
  getApiErrorMessage: (key: string) => string | null;
  hasApiErrors: () => boolean;
  
  // Notification actions - Enhanced for real-time and API integration
  setUnreadCount: (count: number) => void;
  addNotification: (notification: Omit<AppState['notifications'][0], 'id' | 'timestamp' | 'read'>) => void;
  removeNotification: (id: string) => void;
  markNotificationRead: (id: string) => void;
  markAllNotificationsRead: () => void;
  clearAllNotifications: () => void;
  getUnreadNotifications: () => AppState['notifications'];
  
  // API state management - Enhanced
  updateLastApiCall: () => void;
  setCacheTimestamp: (key: string, timestamp?: number) => void;
  isCacheValid: (key: string, maxAge?: number) => boolean;
  clearCache: (pattern?: string) => void;
  addToRequestQueue: (endpoint: string, retry?: number) => void;
  removeFromRequestQueue: (endpoint: string) => void;
  clearRequestQueue: () => void;
  
  // Feature flag management - Enhanced
  setFeatureFlag: (flag: keyof AppState['featureFlags'], value: boolean) => void;
  getFeatureFlag: (flag: keyof AppState['featureFlags']) => boolean;
  
  // Session integration
  setSessionWarning: (shown: boolean) => void;
  setOfflineMode: (offline: boolean) => void;

  // Onboarding actions
  setShowOnboarding: (show: boolean) => void;
  completeOnboarding: () => void;

  // Utility actions
  reset: () => void;
}

type AppStore = AppState & AppActions;

// Initial state with enhanced defaults
const initialState: AppState = {
  // UI State
  sidebarOpen: true,
  theme: 'system',
  
  // Loading states
  globalLoading: false,
  apiLoading: {},
  
  // Error handling
  globalError: null,
  apiErrors: {},
  
  // Notifications
  unreadCount: 0,
  notifications: [],
  
  // API State
  lastApiCall: 0,
  apiCacheTimestamp: {},
  requestQueue: [],
  
  // Feature flags - Updated with current features
  featureFlags: {
    enableRealTimeNotifications: true,
    enableAdvancedSearch: true,
    enableBatchOperations: true,
    enableEmailSync: true,
    enableWorkflowAutomation: false, // New feature, start disabled
  },
  
  // Session integration
  sessionWarningShown: false,
  offlineMode: false,

  // Onboarding
  showOnboarding: false,
  onboardingCompleted: false,
};

export const useAppStore = create<AppStore>()(
  persist(
    (set, get) => ({
      ...initialState,

      // Sidebar actions
      toggleSidebar: () => {
        set((state) => ({ sidebarOpen: !state.sidebarOpen }));
      },

      setSidebarOpen: (open: boolean) => {
        set({ sidebarOpen: open });
      },

      // Theme actions with enhanced system preference handling
      setTheme: (theme: 'light' | 'dark' | 'system') => {
        set({ theme });
        
        // Apply theme to document with improved logic
        const applyThemeToDocument = () => {
          const root = document.documentElement;
          
          if (theme === 'dark') {
            root.classList.add('dark');
            root.setAttribute('data-theme', 'dark');
          } else if (theme === 'light') {
            root.classList.remove('dark');
            root.setAttribute('data-theme', 'light');
          } else {
            // System preference with listener
            const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
            const applySystemTheme = (e?: MediaQueryListEvent) => {
              if ((e?.matches ?? mediaQuery.matches)) {
                root.classList.add('dark');
                root.setAttribute('data-theme', 'dark');
              } else {
                root.classList.remove('dark');
                root.setAttribute('data-theme', 'light');
              }
            };
            
            // Clean up previous listener if exists
            if ((root as any)._themeCleanup) {
              (root as any)._themeCleanup();
            }
            
            // Apply initially and listen for changes
            applySystemTheme();
            mediaQuery.addEventListener('change', applySystemTheme);
            
            // Store cleanup function
            (root as any)._themeCleanup = () => {
              mediaQuery.removeEventListener('change', applySystemTheme);
            };
          }
        };
        
        // Apply theme safely
        if (typeof document !== 'undefined') {
          applyThemeToDocument();
        }
      },

      // Enhanced loading actions for API integration
      setGlobalLoading: (loading: boolean) => {
        set({ globalLoading: loading });
      },

      setApiLoading: (key: string, loading: boolean) => {
        set((state) => {
          if (loading) {
            return {
              apiLoading: { ...state.apiLoading, [key]: true }
            };
          } else {
            const { [key]: removed, ...rest } = state.apiLoading;
            return { apiLoading: rest };
          }
        });
      },

      clearApiLoading: () => {
        set({ apiLoading: {} });
      },

      isAnyApiLoading: () => {
        const { apiLoading } = get();
        return Object.values(apiLoading).some(Boolean);
      },

      // Enhanced error actions for API integration with structured error support
      setGlobalError: (error: string | null) => {
        set({ globalError: error });
        
        // Auto-add to notifications if it's a new error
        if (error) {
          get().addNotification({
            type: 'error',
            title: 'System Error',
            message: error,
            persistent: true,
            category: 'system',
            priority: 'high',
          });
        }
      },

      clearGlobalError: () => {
        set({ globalError: null });
      },

      setApiError: (key: string, error: string | ApiErrorResponse) => {
        set((state) => ({
          apiErrors: {
            ...state.apiErrors,
            [key]: error,
          },
        }));
        
        // Add structured notification for API errors
        const message = typeof error === 'string' ? error : error.message || 'An API error occurred';
        get().addNotification({
          type: 'error',
          title: 'API Error',
          message,
          category: 'system',
          priority: typeof error === 'object' && error.status >= 500 ? 'high' : 'medium',
        });
      },

      clearApiError: (key: string) => {
        set((state) => {
          const { [key]: removed, ...rest } = state.apiErrors;
          return { apiErrors: rest };
        });
      },

      clearAllApiErrors: () => {
        set({ apiErrors: {} });
      },

      getApiErrorMessage: (key: string) => {
        const { apiErrors } = get();
        const error = apiErrors[key];
        if (!error) return null;
        return typeof error === 'string' ? error : error.message || 'Unknown API error';
      },

      hasApiErrors: () => {
        const { apiErrors } = get();
        return Object.keys(apiErrors).length > 0;
      },

      // Enhanced notification actions with API integration
      setUnreadCount: (count: number) => {
        set({ unreadCount: count });
      },

      addNotification: (notification) => {
        const id = `notification_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        const newNotification = {
          ...notification,
          id,
          timestamp: Date.now(),
          read: false,
        };
        
        set((state) => ({
          notifications: [newNotification, ...state.notifications.slice(0, 99)], // Keep max 100
          unreadCount: state.unreadCount + 1,
        }));

        // Auto-remove non-persistent notifications after timeout based on priority
        if (!notification.persistent) {
          const timeout = notification.priority === 'high' ? 10000 : 
                         notification.priority === 'medium' ? 7000 : 5000;
          setTimeout(() => {
            get().removeNotification(id);
          }, timeout);
        }
      },

      removeNotification: (id: string) => {
        set((state) => {
          const notification = state.notifications.find(n => n.id === id);
          return {
            notifications: state.notifications.filter((n) => n.id !== id),
            unreadCount: notification && !notification.read 
              ? Math.max(0, state.unreadCount - 1) 
              : state.unreadCount,
          };
        });
      },

      markNotificationRead: (id: string) => {
        set((state) => {
          const notification = state.notifications.find(n => n.id === id);
          return {
            notifications: state.notifications.map((n) =>
              n.id === id ? { ...n, read: true } : n
            ),
            unreadCount: notification && !notification.read 
              ? Math.max(0, state.unreadCount - 1) 
              : state.unreadCount,
          };
        });
      },

      markAllNotificationsRead: () => {
        set((state) => ({
          notifications: state.notifications.map((n) => ({ ...n, read: true })),
          unreadCount: 0,
        }));
      },

      clearAllNotifications: () => {
        set({ notifications: [], unreadCount: 0 });
      },

      getUnreadNotifications: () => {
        const { notifications } = get();
        return notifications.filter(n => !n.read);
      },

      // Enhanced API state management
      updateLastApiCall: () => {
        set({ lastApiCall: Date.now() });
      },

      setCacheTimestamp: (key: string, timestamp?: number) => {
        set((state) => ({
          apiCacheTimestamp: {
            ...state.apiCacheTimestamp,
            [key]: timestamp || Date.now(),
          },
        }));
      },

      isCacheValid: (key: string, maxAge: number = 300000) => { // 5 minutes default
        const timestamp = get().apiCacheTimestamp[key];
        if (!timestamp) return false;
        return Date.now() - timestamp < maxAge;
      },

      clearCache: (pattern?: string) => {
        set((state) => {
          if (!pattern) {
            return { apiCacheTimestamp: {} };
          }
          
          const filteredCache: Record<string, number> = {};
          Object.entries(state.apiCacheTimestamp).forEach(([key, value]) => {
            if (!key.includes(pattern)) {
              filteredCache[key] = value;
            }
          });
          
          return { apiCacheTimestamp: filteredCache };
        });
      },

      addToRequestQueue: (endpoint: string, retry: number = 0) => {
        set((state) => ({
          requestQueue: [
            ...state.requestQueue.filter(req => req.endpoint !== endpoint),
            {
              id: `req_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
              endpoint,
              retry,
              timestamp: Date.now(),
            },
          ],
        }));
      },

      removeFromRequestQueue: (endpoint: string) => {
        set((state) => ({
          requestQueue: state.requestQueue.filter(req => req.endpoint !== endpoint),
        }));
      },

      clearRequestQueue: () => {
        set({ requestQueue: [] });
      },

      // Enhanced feature flag management
      setFeatureFlag: (flag, value) => {
        set((state) => ({
          featureFlags: {
            ...state.featureFlags,
            [flag]: value,
          },
        }));
      },

      getFeatureFlag: (flag) => {
        return get().featureFlags[flag];
      },

      // Session integration
      setSessionWarning: (shown: boolean) => {
        set({ sessionWarningShown: shown });
      },

      setOfflineMode: (offline: boolean) => {
        set({ offlineMode: offline });
        
        if (offline) {
          get().addNotification({
            type: 'warning',
            title: 'Offline Mode',
            message: 'You are currently offline. Some features may be limited.',
            persistent: true,
            category: 'system',
            priority: 'medium',
          });
        }
      },

      // Onboarding actions
      setShowOnboarding: (show: boolean) => {
        set({ showOnboarding: show });
      },

      completeOnboarding: () => {
        localStorage.setItem('workidoc_onboarding_completed', 'true');
        set({ showOnboarding: false, onboardingCompleted: true });
      },

      // Utility actions
      reset: () => {
        // Clean up theme listeners before reset
        if (typeof document !== 'undefined') {
          const root = document.documentElement;
          if ((root as any)._themeCleanup) {
            (root as any)._themeCleanup();
          }
        }
        
        set(initialState);
      },
    }),
    {
      name: 'app-storage',
      // Enhanced partialize to include new state that should persist
      partialize: (state) => ({
        sidebarOpen: state.sidebarOpen,
        theme: state.theme,
        featureFlags: state.featureFlags,
        onboardingCompleted: state.onboardingCompleted,
        // Don't persist loading states, errors, notifications, or temporary data
      }),
      version: 3, // Increment version for migration
      migrate: (persistedState: any, version: number) => {
        // Handle migration from older versions
        if (version < 3) {
          return {
            ...initialState,
            ...persistedState,
            featureFlags: {
              ...initialState.featureFlags,
              ...(persistedState?.featureFlags || {}),
            },
          };
        }
        return persistedState;
      },
    }
  )
);

// Export enhanced helper hooks for common use cases
export const useGlobalLoading = () => useAppStore((state) => state.globalLoading);
export const useApiLoading = (key?: string) => {
  const apiLoading = useAppStore((state) => state.apiLoading);
  const isAnyApiLoading = useAppStore((state) => state.isAnyApiLoading);
  return key ? apiLoading[key] || false : isAnyApiLoading();
};
export const useGlobalError = () => useAppStore((state) => state.globalError);
export const useApiError = (key: string) => useAppStore((state) => state.getApiErrorMessage(key));
export const useApiErrors = () => useAppStore((state) => state.hasApiErrors());
export const useNotifications = () => useAppStore((state) => state.notifications);
export const useUnreadNotifications = () => useAppStore((state) => state.getUnreadNotifications());
export const useUnreadCount = () => useAppStore((state) => state.unreadCount);
export const useFeatureFlag = (flag: keyof AppState['featureFlags']) => 
  useAppStore((state) => state.getFeatureFlag(flag));
export const useOfflineMode = () => useAppStore((state) => state.offlineMode);

// Export enhanced action selectors
export const useAppActions = () => useAppStore((state) => ({
  // UI actions
  toggleSidebar: state.toggleSidebar,
  setSidebarOpen: state.setSidebarOpen,
  setTheme: state.setTheme,
  
  // Loading actions
  setGlobalLoading: state.setGlobalLoading,
  setApiLoading: state.setApiLoading,
  clearApiLoading: state.clearApiLoading,
  isAnyApiLoading: state.isAnyApiLoading,
  
  // Error actions
  setGlobalError: state.setGlobalError,
  clearGlobalError: state.clearGlobalError,
  setApiError: state.setApiError,
  clearApiError: state.clearApiError,
  clearAllApiErrors: state.clearAllApiErrors,
  getApiErrorMessage: state.getApiErrorMessage,
  hasApiErrors: state.hasApiErrors,
  
  // Notification actions
  addNotification: state.addNotification,
  removeNotification: state.removeNotification,
  markNotificationRead: state.markNotificationRead,
  markAllNotificationsRead: state.markAllNotificationsRead,
  clearAllNotifications: state.clearAllNotifications,
  getUnreadNotifications: state.getUnreadNotifications,
  
  // API actions
  updateLastApiCall: state.updateLastApiCall,
  setCacheTimestamp: state.setCacheTimestamp,
  isCacheValid: state.isCacheValid,
  clearCache: state.clearCache,
  addToRequestQueue: state.addToRequestQueue,
  removeFromRequestQueue: state.removeFromRequestQueue,
  clearRequestQueue: state.clearRequestQueue,
  
  // Feature flags
  setFeatureFlag: state.setFeatureFlag,
  getFeatureFlag: state.getFeatureFlag,
  
  // Session integration
  setSessionWarning: state.setSessionWarning,
  setOfflineMode: state.setOfflineMode,

  // Onboarding
  setShowOnboarding: state.setShowOnboarding,
  completeOnboarding: state.completeOnboarding,

  // Utility
  reset: state.reset,
}));

export default useAppStore;